import { useState } from 'react';
import { toast } from 'sonner';

import {
  mensagemDoErro,
  PUBLICOS_ALVO,
  ROTULO_DO_PUBLICO,
  useCriarComunicado,
  type NovoComunicado,
  type PublicoAlvo,
} from '@/lib/useComunicados';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/ui/base/dialog';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Switch } from '@/ui/base/switch';
import { Textarea } from '@/ui/base/textarea';
import { FormFieldWrapper } from '@/ui/FormField';

interface Campos {
  titulo: string;
  resumo: string;
  conteudoMarkdown: string;
  publicoAlvo: PublicoAlvo;
  vigenciaInicio: string;
  vigenciaFim: string;
  exigeConfirmacao: boolean;
}

/** Data de hoje no fuso do navegador, `aaaa-mm-dd`. */
function hoje(): string {
  const d = new Date();
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
}

const novaChave = () => `comunicado-${crypto.randomUUID()}`;

/**
 * Formulário do rascunho de comunicado. Monta de novo a cada abertura (o pai
 * troca a `key`), então nada sobra da vez anterior.
 *
 * A chave de idempotência acompanha o conteúdo: repetir o envio sem mudar nada
 * (queda de rede, clique duplo) devolve o mesmo rascunho; mexer em qualquer
 * campo é outro pedido e ganha chave nova — com a mesma chave e outro texto a
 * API recusaria por conflito.
 */
export function FormularioDeComunicado({
  aberto,
  aoMudarAberto,
}: {
  aberto: boolean;
  aoMudarAberto: (aberto: boolean) => void;
}) {
  const criar = useCriarComunicado();
  const [campos, setCampos] = useState<Campos>({
    titulo: '',
    resumo: '',
    conteudoMarkdown: '',
    publicoAlvo: 'CREDENCIADO',
    vigenciaInicio: hoje(),
    vigenciaFim: '',
    exigeConfirmacao: true,
  });
  const [chave, setChave] = useState(novaChave);
  const [erroDoEnvio, setErroDoEnvio] = useState<string | null>(null);

  function alterar<K extends keyof Campos>(campo: K, valor: Campos[K]) {
    setCampos((atuais) => ({ ...atuais, [campo]: valor }));
    setChave(novaChave());
    setErroDoEnvio(null);
  }

  const vigenciaInvertida = campos.vigenciaFim !== '' && campos.vigenciaFim < campos.vigenciaInicio;
  const completo =
    campos.titulo.trim().length >= 3 &&
    campos.conteudoMarkdown.trim().length > 0 &&
    campos.vigenciaInicio !== '' &&
    !vigenciaInvertida;

  function salvar() {
    const novo: NovoComunicado = {
      titulo: campos.titulo.trim(),
      resumo: campos.resumo.trim() || null,
      conteudoMarkdown: campos.conteudoMarkdown.trim(),
      publicoAlvo: campos.publicoAlvo,
      vigenciaInicio: campos.vigenciaInicio,
      vigenciaFim: campos.vigenciaFim || null,
      exigeConfirmacao: campos.exigeConfirmacao,
      chaveIdempotencia: chave,
    };
    criar.mutate(novo, {
      onSuccess: () => {
        toast.success('Rascunho salvo. Publique quando o texto estiver pronto.');
        aoMudarAberto(false);
      },
      onError: (erro) =>
        setErroDoEnvio(
          mensagemDoErro(erro, 'Não foi possível salvar o comunicado. Tente de novo.'),
        ),
    });
  }

  return (
    <Dialog open={aberto} onOpenChange={aoMudarAberto}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Novo comunicado oficial</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <FormFieldWrapper label="Título" htmlFor="comunicado-titulo" required>
            <Input
              id="comunicado-titulo"
              value={campos.titulo}
              maxLength={200}
              onChange={(e) => alterar('titulo', e.target.value)}
            />
          </FormFieldWrapper>

          <FormFieldWrapper label="Resumo" htmlFor="comunicado-resumo">
            <Input
              id="comunicado-resumo"
              value={campos.resumo}
              maxLength={500}
              onChange={(e) => alterar('resumo', e.target.value)}
            />
          </FormFieldWrapper>

          <FormFieldWrapper label="Conteúdo (markdown)" htmlFor="comunicado-conteudo" required>
            <Textarea
              id="comunicado-conteudo"
              rows={8}
              maxLength={20000}
              value={campos.conteudoMarkdown}
              onChange={(e) => alterar('conteudoMarkdown', e.target.value)}
            />
          </FormFieldWrapper>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormFieldWrapper label="Público-alvo" htmlFor="comunicado-publico">
              <Select
                value={campos.publicoAlvo}
                onValueChange={(v) => alterar('publicoAlvo', v as PublicoAlvo)}
              >
                <SelectTrigger id="comunicado-publico">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PUBLICOS_ALVO.map((p) => (
                    <SelectItem key={p} value={p}>
                      {ROTULO_DO_PUBLICO[p]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormFieldWrapper>

            <div className="space-y-1.5 pt-1 sm:pt-6">
              <div className="flex items-center gap-2">
                <Switch
                  id="comunicado-confirmacao"
                  checked={campos.exigeConfirmacao}
                  onCheckedChange={(v) => alterar('exigeConfirmacao', v)}
                />
                <Label htmlFor="comunicado-confirmacao">Exige confirmação de leitura</Label>
              </div>
              <p className="text-[11px] text-muted-foreground">
                A confirmação pelos destinatários ainda não está disponível; a opção fica registrada
                no comunicado.
              </p>
            </div>

            <FormFieldWrapper label="Vigência início" htmlFor="comunicado-inicio" required>
              <Input
                id="comunicado-inicio"
                type="date"
                value={campos.vigenciaInicio}
                onChange={(e) => alterar('vigenciaInicio', e.target.value)}
              />
            </FormFieldWrapper>

            <FormFieldWrapper
              label="Vigência fim (opcional)"
              htmlFor="comunicado-fim"
              error={vigenciaInvertida ? 'O fim não pode ser antes do início.' : undefined}
            >
              <Input
                id="comunicado-fim"
                type="date"
                value={campos.vigenciaFim}
                onChange={(e) => alterar('vigenciaFim', e.target.value)}
              />
            </FormFieldWrapper>
          </div>

          {erroDoEnvio && (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{erroDoEnvio}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => aoMudarAberto(false)}>
            Cancelar
          </Button>
          <Button onClick={salvar} disabled={!completo || criar.isPending}>
            Salvar rascunho
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
